import { useCallback, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  DEFAULT_VERTICAL,
  VERTICALS,
  VERTICAL_STORAGE_KEY,
  VerticalContext,
  type Vertical,
} from './useVertical'

/**
 * Vertical ids this browser may still hold from a previous release, mapped to the current
 * one. Without this, everyone whose last visit was to Stays silently lands back on
 * Farm-to-Table after the rename, because the stored `homestay` no longer matches any
 * vertical. Entries can be dropped once no browser plausibly holds the old value.
 */
const LEGACY_IDS: Record<string, Vertical> = {
  homestay: 'stays',
  homestay_nagaland: 'stays',
  agri_d2c: 'farm_to_table',
}

function readStoredVertical(): Vertical {
  try {
    const stored = localStorage.getItem(VERTICAL_STORAGE_KEY)
    if (!stored) return DEFAULT_VERTICAL
    if (VERTICALS.some((v) => v.id === stored)) return stored as Vertical

    const migrated = LEGACY_IDS[stored]
    if (migrated) {
      // Rewrite it, so the mapping is needed once per browser rather than every load.
      localStorage.setItem(VERTICAL_STORAGE_KEY, migrated)
      return migrated
    }
  } catch {
    // localStorage unavailable (SSR/private mode) — fall back to default.
  }
  return DEFAULT_VERTICAL
}

export function VerticalProvider({ children }: { children: ReactNode }) {
  const [activeVertical, setActive] = useState<Vertical>(readStoredVertical)

  const setVertical = useCallback((v: Vertical) => {
    setActive(v)
    try {
      localStorage.setItem(VERTICAL_STORAGE_KEY, v)
    } catch {
      // Persistence is best-effort; in-memory state still works.
    }
  }, [])

  const value = useMemo(() => ({ activeVertical, setVertical }), [activeVertical, setVertical])

  return <VerticalContext.Provider value={value}>{children}</VerticalContext.Provider>
}
